// src/lib/pipeline-service.ts
// Per-user capture pipeline for contracts (watching -> pursuing -> submitted ->
// won/lost/skipped). Backed by the contract_pipeline table (RLS owner-only).

import { supabase } from './supabase';

export type PipelineStage = 'watching' | 'pursuing' | 'submitted' | 'won' | 'lost' | 'skipped';

// Ordered lifecycle stages (excludes 'skipped', which is a terminal side-state).
export const PIPELINE_STAGES: PipelineStage[] = ['watching', 'pursuing', 'submitted', 'won', 'lost'];

export const STAGE_META: Record<PipelineStage, { label: string; pill: string; dot: string }> = {
  watching:  { label: 'Watching',  pill: 'bg-bw-surface-subtle text-bw-muted border-bw-border',         dot: 'bg-bw-muted' },
  pursuing:  { label: 'Pursuing',  pill: 'bg-bw-primary-tint text-bw-primary border-bw-primary/30',     dot: 'bg-bw-primary' },
  submitted: { label: 'Submitted', pill: 'bg-fit-moderate-bg text-fit-moderate border-amber-300',       dot: 'bg-fit-moderate' },
  won:       { label: 'Won',       pill: 'bg-success-bg text-success border-green-300',                  dot: 'bg-success' },
  lost:      { label: 'Lost',      pill: 'bg-urgent-bg text-urgent border-red-300',                      dot: 'bg-urgent' },
  skipped:   { label: 'Skipped',   pill: 'bg-bw-surface-subtle text-bw-muted border-bw-border',         dot: 'bg-bw-muted' },
};

export interface PipelineSummary {
  counts: Record<PipelineStage, number>;
  total: number;          // anything actively in the pipeline (excludes skipped)
  winRate: number | null; // won / (won + lost), null when no decided bids
  pipelineValue: number;  // sum of award_value for won
}

/** Map contract_id -> stage for a set of contracts (for badges on cards). */
export async function getPipelineStages(userId: string, contractIds: string[]): Promise<Map<string, PipelineStage>> {
  const out = new Map<string, PipelineStage>();
  if (!userId || contractIds.length === 0) return out;
  try {
    for (let i = 0; i < contractIds.length; i += 200) {
      const chunk = contractIds.slice(i, i + 200);
      const { data } = await supabase
        .from('contract_pipeline')
        .select('contract_id, stage')
        .eq('user_id', userId)
        .in('contract_id', chunk);
      (data || []).forEach((r: any) => out.set(r.contract_id, r.stage as PipelineStage));
    }
  } catch (e) {
    console.warn('getPipelineStages failed:', e);
  }
  return out;
}

/** Funnel + win-rate + pipeline-value summary across the user's whole pipeline. */
export async function getPipelineSummary(userId: string): Promise<PipelineSummary> {
  const counts: Record<PipelineStage, number> = {
    watching: 0, pursuing: 0, submitted: 0, won: 0, lost: 0, skipped: 0,
  };
  let pipelineValue = 0;
  try {
    const { data } = await supabase
      .from('contract_pipeline')
      .select('stage, award_value')
      .eq('user_id', userId);
    (data || []).forEach((r: any) => {
      const s = r.stage as PipelineStage;
      if (s in counts) counts[s] += 1;
      if (s === 'won' && r.award_value) pipelineValue += Number(r.award_value) || 0;
    });
  } catch (e) {
    console.warn('getPipelineSummary failed:', e);
  }
  const decided = counts.won + counts.lost;
  return {
    counts,
    total: counts.watching + counts.pursuing + counts.submitted + counts.won + counts.lost,
    winRate: decided > 0 ? counts.won / decided : null,
    pipelineValue,
  };
}

/** Upsert a contract's stage. Pass null stage to remove it from the pipeline. */
export async function setContractStage(
  userId: string,
  contractId: string,
  stage: PipelineStage | null,
  awardValue?: number | null,
): Promise<void> {
  if (stage === null) {
    await supabase.from('contract_pipeline').delete().eq('user_id', userId).eq('contract_id', contractId);
    return;
  }
  await supabase.from('contract_pipeline').upsert(
    {
      user_id: userId,
      contract_id: contractId,
      stage,
      ...(awardValue !== undefined ? { award_value: awardValue } : {}),
    },
    { onConflict: 'user_id,contract_id' },
  );
}
